import { Fragment, type JSX, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, FolderKanban, GitBranch, ListTodo, Timer } from 'lucide-react';

import { STATUS_DISPLAY_LABEL } from '~/hooks/useProjectFiltering';
import {
  useAllRepositoryProjects,
  useRepositories,
  useRepository,
  useRepositoryProjects,
} from '~/hooks/useRepositories';
import { useUIStore } from '~/stores/uiStore';

import { ActivityChart } from '~/components/charts/ActivityChart';

import type { ProjectWithTasks, Repository } from '~/types';

// --- Shared analytics helpers ---

interface RepoAnalytics {
  activeProjects: number;
  avgCycleTimeDays: number | null;
  completedProjects: number;
  completedTasks: number;
  taskCompletionRate: number;
  taskStatusBreakdown: { done: number; inProgress: number; todo: number };
  totalProjects: number;
  totalTasks: number;
}

function computeRepoAnalytics(projects: ProjectWithTasks[]): RepoAnalytics {
  let totalTasks = 0;
  let completedTasks = 0;
  let todo = 0;
  const inProgress = 0;
  let done = 0;
  let activeProjects = 0;
  let completedProjects = 0;
  let cycleTimeSum = 0;
  let cycleTimeCount = 0;

  for (const project of projects) {
    if (project.status !== 'DONE') activeProjects++;
    if (project.status === 'DONE') {
      completedProjects++;
      const created = new Date(project.createdAt).getTime();
      const updated = new Date(project.updatedAt).getTime();
      if (updated > created) {
        cycleTimeSum += (updated - created) / (1000 * 60 * 60 * 24);
        cycleTimeCount++;
      }
    }

    // Use pre-computed counts from the API instead of iterating embedded tasks
    const projTotal = project.totalTasks ?? 0;
    const projCompleted = project.completedTasks ?? 0;
    totalTasks += projTotal;
    completedTasks += projCompleted;
    done += projCompleted;
    // Without per-task status breakdown from the API, remaining tasks are counted as todo
    todo += projTotal - projCompleted;
  }

  return {
    activeProjects,
    avgCycleTimeDays:
      cycleTimeCount > 0 ? Math.round((cycleTimeSum / cycleTimeCount) * 10) / 10 : null,
    completedProjects,
    completedTasks,
    taskCompletionRate: totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 1000) / 10 : 0,
    taskStatusBreakdown: { done, inProgress, todo },
    totalProjects: projects.length,
    totalTasks,
  };
}

// ============================================================
// Repository Dashboard (no selection)
// ============================================================

function DashboardHeader({ repoCount }: { repoCount: number }): JSX.Element {
  return (
    <div className="mb-6">
      <h1 className="text-xl font-semibold text-foreground">Repository Dashboard</h1>
      <p className="text-sm text-muted mt-1">
        {repoCount} repositor{repoCount !== 1 ? 'ies' : 'y'} across your organization
      </p>
    </div>
  );
}

function DashboardSkeleton(): JSX.Element {
  return (
    <div className="p-6 animate-pulse">
      <div className="h-6 w-48 bg-surface-elevated rounded mb-2" />
      <div className="h-4 w-64 bg-surface-elevated rounded mb-6" />
      <div className="bg-surface rounded-lg mb-6">
        <div className="flex flex-wrap">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="w-1/2 px-5 py-4">
              <div className="h-7 w-16 bg-surface-elevated rounded mb-1" />
              <div className="h-3 w-20 bg-surface-elevated rounded" />
            </div>
          ))}
        </div>
      </div>
      <div className="space-y-0">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="flex items-center px-3 py-3 border-b border-white/5">
            <div className="w-6 flex justify-center">
              <div className="w-4 h-4 bg-surface-elevated rounded" />
            </div>
            <div className="flex-1">
              <div className="h-4 w-40 bg-surface-elevated rounded" />
            </div>
            <div className="w-28">
              <div className="h-3 w-16 bg-surface-elevated rounded ml-auto" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DashboardStatStrip({ repos }: { repos: Repository[] }): JSX.Element {
  const totalProjects = repos.reduce((sum, r) => sum + (r.projectCount ?? 0), 0);

  const metrics = [
    { label: 'Repositories', value: String(repos.length) },
    { label: 'Total Projects', value: String(totalProjects) },
  ];

  return (
    <div className="bg-surface rounded-lg mb-6">
      <div className="flex flex-wrap">
        {metrics.map((m, i) => (
          <Fragment key={m.label}>
            {i > 0 && <div className="hidden md:block w-px bg-white/5" />}
            <div className="w-1/2 md:flex-1 px-5 py-4">
              <p className="text-2xl font-semibold text-foreground tabular-nums">{m.value}</p>
              <p className="text-xs text-muted mt-1">{m.label}</p>
            </div>
          </Fragment>
        ))}
      </div>
    </div>
  );
}

function ProjectDistribution({ repos }: { repos: Repository[] }): JSX.Element | null {
  const sorted = useMemo(
    () =>
      [...repos]
        .filter(r => (r.projectCount ?? 0) > 0)
        .sort((a, b) => (b.projectCount ?? 0) - (a.projectCount ?? 0)),
    [repos],
  );

  if (sorted.length === 0) return null;

  const maxCount = Math.max(...sorted.map(r => r.projectCount ?? 0), 1);

  return (
    <div className="mb-6">
      <h2 className="text-sm font-semibold text-foreground mb-3">Project Distribution</h2>
      <div className="rounded-lg border border-white/5 bg-surface p-4 space-y-3">
        {sorted.map(repo => {
          const count = repo.projectCount ?? 0;
          const pct = (count / maxCount) * 100;
          return (
            <div key={repo.id}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-foreground truncate mr-4">{repo.fullName}</span>
                <span className="text-xs text-muted tabular-nums shrink-0">
                  {count} project{count !== 1 ? 's' : ''}
                </span>
              </div>
              <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary rounded-full transition-all"
                  style={{ width: `${Math.max(pct, 4)}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RepoDashboard(): JSX.Element {
  const { data: repos, isLoading } = useRepositories();
  const { allProjects } = useAllRepositoryProjects(repos);

  if (isLoading) return <DashboardSkeleton />;

  if (!repos || repos.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted">
        <GitBranch className="w-12 h-12 mb-3 opacity-40" />
        <p className="text-sm">No repositories yet.</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <DashboardHeader repoCount={repos.length} />
      <DashboardStatStrip repos={repos} />
      <ThroughputCharts projects={allProjects} />
      <RepoTable repos={repos} />
      <ProjectDistribution repos={repos} />
    </div>
  );
}

function RepoDetailSkeleton(): JSX.Element {
  return (
    <div className="p-6 animate-pulse">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-6 h-6 bg-surface-elevated rounded" />
        <div>
          <div className="h-6 w-48 bg-surface-elevated rounded mb-1" />
          <div className="h-4 w-64 bg-surface-elevated rounded" />
        </div>
      </div>
      <div className="grid grid-cols-4 gap-3 mb-6">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="rounded-lg border border-white/5 bg-surface px-4 py-3">
            <div className="h-3 w-16 bg-surface-elevated rounded mb-2" />
            <div className="h-7 w-10 bg-surface-elevated rounded" />
          </div>
        ))}
      </div>
      <div className="rounded-lg border border-white/5 bg-surface p-4 mb-6">
        <div className="h-4 w-24 bg-surface-elevated rounded mb-3" />
        <div className="grid grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <div key={i}>
              <div className="h-3 w-20 bg-surface-elevated rounded mb-1" />
              <div className="h-6 w-12 bg-surface-elevated rounded" />
            </div>
          ))}
        </div>
      </div>
      <div className="space-y-2">
        {[...Array(3)].map((_, i) => (
          <div
            key={i}
            className="rounded-lg border border-white/5 bg-surface px-4 py-3 border-l-3 border-l-surface-elevated"
          >
            <div className="h-4 w-48 bg-surface-elevated rounded mb-1" />
            <div className="h-3 w-72 bg-surface-elevated rounded" />
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================
// Selected Repository Detail (dashboard style)
// ============================================================

function RepoHeader({ description, name }: { description?: string; name: string }): JSX.Element {
  return (
    <div className="mb-6">
      <div className="flex items-center gap-3">
        <GitBranch className="w-6 h-6 text-muted" />
        <div>
          <h1 className="text-xl font-semibold text-foreground">{name}</h1>
          {description && <p className="text-sm text-muted mt-1">{description}</p>}
        </div>
      </div>
    </div>
  );
}

function RepoKeyMetrics({
  analytics,
  projectCount,
}: {
  analytics: RepoAnalytics;
  projectCount: number;
}): JSX.Element {
  const completion = `${analytics.taskCompletionRate}%`;
  const cycleTime = analytics.avgCycleTimeDays !== null ? `${analytics.avgCycleTimeDays}d` : '—';
  const wip = analytics.taskStatusBreakdown.inProgress;

  const stats = [
    { icon: <FolderKanban className="w-4 h-4" />, label: 'Projects', value: String(projectCount) },
    { icon: <CheckCircle2 className="w-4 h-4" />, label: 'Task Completion', value: completion },
    { icon: <Timer className="w-4 h-4" />, label: 'Avg Cycle Time', value: cycleTime },
    { icon: <ListTodo className="w-4 h-4" />, label: 'In Progress', value: String(wip) },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
      {stats.map(s => (
        <div key={s.label} className="rounded-lg border border-white/5 bg-surface px-4 py-3">
          <div className="flex items-center gap-2 text-muted mb-1">
            {s.icon}
            <span className="text-xs">{s.label}</span>
          </div>
          <p className="text-2xl font-semibold text-foreground tabular-nums">{s.value}</p>
        </div>
      ))}
    </div>
  );
}

function RepoProjectList({ projects }: { projects: ProjectWithTasks[] }): JSX.Element | null {
  const navigate = useNavigate();

  const sorted = useMemo(() => {
    if (projects.length === 0) return [];
    const order: Record<string, number> = {
      IN_PROGRESS: 0,
      SCOPING: 1,
      TASKING: 1,
      TODO: 1,
      DONE: 2,
    };
    return [...projects].sort((a, b) => (order[a.status] ?? 5) - (order[b.status] ?? 5));
  }, [projects]);

  if (sorted.length === 0) return null;

  return (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-3">
        <h2 className="text-sm font-semibold text-foreground">Projects</h2>
        <span className="px-2 py-0.5 text-xs bg-white/5 text-muted rounded-full">
          {sorted.length}
        </span>
      </div>
      <div className="space-y-2">
        {sorted.map(project => {
          const borderColor =
            project.status === 'DONE'
              ? 'border-l-success'
              : project.status === 'IN_PROGRESS'
                ? 'border-l-primary'
                : 'border-l-muted/40';
          const badgeClass =
            project.status === 'DONE'
              ? 'text-success'
              : project.status === 'IN_PROGRESS'
                ? 'text-primary'
                : 'text-muted';

          const totalTasks = project.totalTasks ?? 0;
          const doneTasks = project.completedTasks ?? 0;

          return (
            <button
              key={project.id}
              type="button"
              onClick={() => void navigate(`/projects/${project.id}`)}
              className={`w-full flex items-center justify-between rounded-lg border border-white/5 bg-surface px-4 py-3 text-left hover:bg-white/5 transition-colors border-l-3 ${borderColor}`}
            >
              <div className="min-w-0 flex-1 mr-4">
                <p className="text-sm font-medium text-foreground truncate">{project.title}</p>
                {project.description && (
                  <p className="text-xs text-muted truncate mt-0.5">{project.description}</p>
                )}
              </div>
              <div className="flex items-center gap-3 shrink-0">
                {totalTasks > 0 && (
                  <span className="text-xs text-muted tabular-nums">
                    {doneTasks}/{totalTasks}
                  </span>
                )}
                <span className={`text-xs font-medium uppercase tracking-wide ${badgeClass}`}>
                  {STATUS_DISPLAY_LABEL[project.status] ?? project.status}
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function RepositoryDetailContent({ id }: { id: string }): JSX.Element {
  const { data: repo, isLoading: repoLoading } = useRepository(id);
  const { data: projects, isLoading: projectsLoading } = useRepositoryProjects(id);

  if (repoLoading || projectsLoading) return <RepoDetailSkeleton />;
  if (!repo) return <div className="p-6 text-sm text-error">Repository not found</div>;

  const analytics = computeRepoAnalytics(projects ?? []);

  return (
    <div className="p-6">
      <RepoHeader description={repo.description} name={repo.fullName} />
      <RepoKeyMetrics analytics={analytics} projectCount={projects?.length ?? 0} />
      <ThroughputCharts projects={projects ?? []} />
      <RepoTaskStatusBar analytics={analytics} />
      <RepoProjectList projects={projects ?? []} />
    </div>
  );
}

function RepoTable({ repos }: { repos: Repository[] }): JSX.Element {
  const setSelectedRepositoryId = useUIStore(s => s.setSelectedRepositoryId);

  const sorted = useMemo(
    () => [...repos].sort((a, b) => (b.projectCount ?? 0) - (a.projectCount ?? 0)),
    [repos],
  );

  return (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-3">
        <h2 className="text-sm font-semibold text-foreground">Repositories</h2>
        <span className="px-2 py-0.5 text-xs bg-white/5 text-muted rounded-full">
          {repos.length}
        </span>
      </div>

      {/* Column headers */}
      <div className="hidden md:flex items-center px-3 py-2 text-xs text-muted/60 uppercase tracking-wider">
        <div className="w-6" />
        <div className="flex-1">Name</div>
        <div className="w-28 text-right">Projects</div>
      </div>

      {/* Rows */}
      <div>
        {sorted.map(repo => (
          <button
            key={repo.id}
            type="button"
            className="w-full flex items-center px-3 py-3 border-b border-white/5 hover:bg-white/5 transition-colors text-left"
            onClick={() => setSelectedRepositoryId(repo.id)}
          >
            <div className="w-6 flex items-center justify-center">
              <GitBranch className="w-4 h-4 text-muted" />
            </div>
            <div className="flex-1 min-w-0">
              <span className="text-sm font-medium text-foreground truncate block">
                {repo.fullName}
              </span>
              {repo.description && (
                <span className="text-xs text-muted truncate block mt-0.5">{repo.description}</span>
              )}
            </div>
            <div className="w-28 text-right">
              <span className="text-xs text-muted">
                {repo.projectCount ?? 0} project{(repo.projectCount ?? 0) !== 1 ? 's' : ''}
              </span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function RepoTaskStatusBar({ analytics }: { analytics: RepoAnalytics }): JSX.Element | null {
  if (analytics.totalTasks === 0) return null;

  const { done, inProgress, todo } = analytics.taskStatusBreakdown;
  const total = analytics.totalTasks;
  const donePct = (done / total) * 100;
  const inProgressPct = (inProgress / total) * 100;
  const todoPct = (todo / total) * 100;

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-sm font-semibold text-foreground">Task Progress</h2>
        <span className="text-xs text-muted tabular-nums">
          {done}/{total} done
        </span>
      </div>
      <div className="flex h-2 rounded-full overflow-hidden bg-white/5">
        {donePct > 0 && (
          <div className="bg-success transition-all" style={{ width: `${donePct}%` }} />
        )}
        {inProgressPct > 0 && (
          <div className="bg-primary transition-all" style={{ width: `${inProgressPct}%` }} />
        )}
        {todoPct > 0 && (
          <div className="bg-white/10 transition-all" style={{ width: `${todoPct}%` }} />
        )}
      </div>
      <div className="flex items-center gap-4 mt-2">
        <span className="flex items-center gap-1.5 text-xs text-muted">
          <span className="w-2 h-2 rounded-full bg-success" />
          Done ({done})
        </span>
        <span className="flex items-center gap-1.5 text-xs text-muted">
          <span className="w-2 h-2 rounded-full bg-primary" />
          In Progress ({inProgress})
        </span>
        <span className="flex items-center gap-1.5 text-xs text-muted">
          <span className="w-2 h-2 rounded-full bg-white/10" />
          To Do ({todo})
        </span>
      </div>
    </div>
  );
}

function ThroughputCharts({ projects }: { projects: ProjectWithTasks[] }): JSX.Element {
  const taskData = useMemo(() => {
    const now = new Date();
    const result: Array<{ label: string; value: number }> = [];

    for (let i = 6; i >= 0; i--) {
      const dayStart = new Date(now);
      dayStart.setDate(dayStart.getDate() - i);
      dayStart.setHours(0, 0, 0, 0);

      const dayEnd = new Date(dayStart);
      dayEnd.setHours(23, 59, 59, 999);

      let count = 0;
      for (const p of projects) {
        for (const t of p.tasks ?? []) {
          if (t.status === 'DONE') {
            const d = new Date(t.updatedAt);
            if (d >= dayStart && d <= dayEnd) count++;
          }
        }
      }

      result.push({
        label: dayStart.toLocaleDateString('en-US', { weekday: 'short' }),
        value: count,
      });
    }

    return result;
  }, [projects]);

  const projectData = useMemo(() => {
    const now = new Date();
    const result: Array<{ label: string; value: number }> = [];

    for (let i = 6; i >= 0; i--) {
      const dayStart = new Date(now);
      dayStart.setDate(dayStart.getDate() - i);
      dayStart.setHours(0, 0, 0, 0);

      const dayEnd = new Date(dayStart);
      dayEnd.setHours(23, 59, 59, 999);

      let count = 0;
      for (const p of projects) {
        const d = new Date(p.createdAt);
        if (d >= dayStart && d <= dayEnd) count++;
      }

      result.push({
        label: dayStart.toLocaleDateString('en-US', { weekday: 'short' }),
        value: count,
      });
    }

    return result;
  }, [projects]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
      <ActivityChart className="" data={taskData} title="Task Throughput (daily)" />
      <ActivityChart className="" data={projectData} title="Project Throughput (daily)" />
    </div>
  );
}

// --- Main entry ---

export function RepositoryDetail(): JSX.Element {
  const selectedRepositoryId = useUIStore(s => s.selectedRepositoryId);

  if (!selectedRepositoryId) {
    return <RepoDashboard />;
  }

  return <RepositoryDetailContent id={selectedRepositoryId} />;
}
